/* eslint-disable complexity, max-lines */
import {
  fetch_all_assigned_onboarding_cases,
  fetch_onboarding_entity_types,
  fetch_onboarding_execution_config,
  fetch_queue_onboarding_cases
} from '@/services/onboarding_journey_service';
import {
  extract_config_form_bindings,
  extract_config_workflow_bindings,
  get_workflow_binding_id
} from '@/services/onboarding_journey_service_helpers';
import {
  fetch_assign_form_status_data,
  fetch_entity_type_workflow_forms,
  fetch_workflow_statuses
} from '@/services/workflow_service';
import { show_onboarding_system_loader } from '@/views/onboarding_system_views/onboarding_ui_helpers';
import {
  persist_onboarding_journey_filter,
  sync_onboarding_journey_filter_from_route
} from '@/services/onboarding_journey_filter_service';
import {
  applicant_gate_route,
  apply_loaded_sidebar_cases,
  build_applicant_entity_type_items,
  build_case_scope_from_case,
  build_entity_type_item,
  collect_gate_items,
  does_route_query_key_match,
  get_case_status_label,
  get_configured_entity_types,
  get_gate_entity_type_items,
  get_scoped_sidebar_data,
  get_sidebar_journey_query,
  get_sidebar_route_name,
  get_sidebar_source_cases,
  get_workflow_record_id,
  get_workflow_status_group,
  has_query_values,
  is_empty_current_query_value,
  merge_applicant_gate_items,
  normalize_sidebar_cases,
  resolve_hydrated_gate_items,
  should_apply_gate_items,
  unique_applicant_entity_types
} from './config_sidebar_method_helpers';

const SIDEBAR_LOADER_SOURCE = 'onboarding-sidebar';
const ACTIVE_ROUTE_ALIASES = new Map([
  ['MyQueue', ['MyQueue', 'MyQueueForm']],
  ['OnboardingManageVerification', ['OnboardingManageVerification', 'OnboardingVerificationCase']],
  ['OnboardingManageManualVerification', ['OnboardingManageManualVerification', 'OnboardingManualVerificationCase']],
  ['OnboardingRiskReview', ['OnboardingRiskReview', 'OnboardingRiskReviewCase']],
  ['OnboardingEsigningSetup', ['OnboardingEsigningSetup', 'OnboardingEsigningCase', 'OnboardingSigningTracker']]
]);

function ignore_sidebar_refresh_error() {
  return undefined;
}

function has_sidebar_case_data(cases = []) {
  return Array.isArray(cases) && cases.length > 0;
}

function show_sidebar_loader(message = 'Loading sidebar...') {
  window.dispatchEvent(new CustomEvent(`system-loader:show:${SIDEBAR_LOADER_SOURCE}`, { detail: message }));
}

function hide_sidebar_loader(instance) {
  instance.$nextTick(() => {
    window.dispatchEvent(new CustomEvent(`system-loader:hide:${SIDEBAR_LOADER_SOURCE}`));
  });
}

function getActiveRouteNames(route_name) {
  return ACTIVE_ROUTE_ALIASES.get(route_name) || [route_name];
}

function isMyQueueFormRoute(route_name = '') {
  return String(route_name || '').trim() === 'MyQueue';
}

function isStatusQueueRoute(route_name = '') {
  return String(route_name || '').trim() === 'OnboardingGateStatusQueue';
}

function get_form_assignment_id(form = {}) {
  return Number(form?.form_id || form?.id || form?.form?.id || 0);
}

function get_config_form_binding_id(binding = {}) {
  return Number(binding?.form || binding?.form_id || binding?.form?.id || binding?.id || 0);
}

function get_config_form_binding_ids(bindings = []) {
  return new Set(
    (Array.isArray(bindings) ? bindings : [])
      .filter((binding) => binding?.is_active !== false)
      .map(get_config_form_binding_id)
      .filter((form_id) => form_id > 0)
  );
}

function is_form_allowed_for_config(form_id, config_form_ids = new Set()) {
  return !config_form_ids.size || config_form_ids.has(form_id);
}

function get_assigned_workflow_form_ids(forms = [], config_form_ids = new Set()) {
  return (Array.isArray(forms) ? forms : [])
    .filter((form) => form?.isAssigned !== false && form?.is_assigned !== false)
    .map(get_form_assignment_id)
    .filter((form_id) => form_id > 0 && is_form_allowed_for_config(form_id, config_form_ids));
}

function get_status_assignment_order(status = {}, fallback_index = 0) {
  const order_candidates = [
    status?.order,
    status?.workflow_status_order,
    status?.display_order,
    status?.sequence
  ];
  const configured_order = order_candidates.map(Number).find((order) => Number.isFinite(order) && order > 0);
  return configured_order || fallback_index + 1;
}

function get_assigned_status_id(status = {}) {
  return Number(status?.status_id || status?.workflow_status_id || status?.form_status_id || 0);
}

function get_assigned_status_name(status = {}) {
  const values = [
    status?.status_name,
    status?.workflow_status_name,
    status?.status?.status,
    status?.status?.name,
    status?.name,
    status?.status
  ];
  return String(values.find((value) => String(value || '').trim()) || '').trim();
}

function get_assigned_statuses(status_data = {}) {
  const statuses = status_data?.assigned_statuses
    || status_data?.data?.assigned_statuses
    || status_data?.selected_statuses
    || status_data?.data?.selected_statuses
    || [];
  return (Array.isArray(statuses) ? statuses : [])
    .map((status, index) => ({
      status_id: get_assigned_status_id(status),
      status_name: get_assigned_status_name(status),
      order: get_status_assignment_order(status, index)
    }))
    .filter((status) => status.status_id > 0);
}

function add_assigned_status_order(status_order_map, status = {}) {
  const existing_status = status_order_map.get(status.status_id);
  const next_order = Number(status.order || 0);
  if (!existing_status || (next_order > 0 && next_order < existing_status.order)) {
    status_order_map.set(status.status_id, status);
  }
  return status_order_map;
}

function build_form_scoped_statuses(results = []) {
  return results
    .filter((result) => result.status === 'fulfilled')
    .flatMap((result) => get_assigned_statuses(result.value))
    .reduce(add_assigned_status_order, new Map());
}

function map_form_status_group(status_order_map = new Map()) {
  return Array.from(status_order_map.values())
    .sort((left, right) => left.order - right.order)
    .map(({ status_id, status_name, order }) => ({
      status_id,
      status_name,
      order,
      workflow_status_order: order
    }));
}

function get_workflow_status_id(status = {}) {
  return Number(
    status?.status_id
    || status?.workflow_status_id
    || status?.form_status_id
    || status?.status?.id
    || 0
  );
}

function get_configured_workflow_order(status = {}) {
  const order_candidates = [
    status?.order,
    status?.workflow_status_order,
    status?.display_order,
    status?.sequence
  ];
  return order_candidates.map(Number).find((order) => Number.isFinite(order) && order > 0) || null;
}

function apply_workflow_status_order(status = {}, assigned_status = {}) {
  const order = get_configured_workflow_order(status) || assigned_status.order;
  return {
    ...assigned_status,
    status_name: get_assigned_status_name(status) || assigned_status.status_name,
    order,
    workflow_status_order: order
  };
}

function map_workflow_ordered_form_status_group(status_order_map = new Map(), workflow_statuses = []) {
  const ordered_statuses = (Array.isArray(workflow_statuses) ? workflow_statuses : [])
    .map((status) => {
      const status_id = get_workflow_status_id(status);
      const assigned_status = status_order_map.get(status_id);
      return assigned_status ? apply_workflow_status_order(status, assigned_status) : null;
    })
    .filter(Boolean)
    .sort((left, right) => left.order - right.order);

  return ordered_statuses.length ? ordered_statuses : map_form_status_group(status_order_map);
}

function get_fulfilled_config_responses(results = []) {
  return results
    .filter((result) => result.status === 'fulfilled')
    .map((result) => result.value)
    .filter(Boolean);
}

function build_config_workflow_status_requests(config_response) {
  const config_form_ids = get_config_form_binding_ids(extract_config_form_bindings(config_response));
  return extract_config_workflow_bindings(config_response)
    .map((workflow) => Number(get_workflow_binding_id(workflow) || get_workflow_record_id(workflow)))
    .filter((workflow_id) => workflow_id > 0)
    .map((workflow_id) => build_form_scoped_workflow_status_group(workflow_id, config_form_ids));
}

async function build_form_scoped_workflow_status_group(workflow_id, config_form_ids = new Set()) {
  const workflow_forms = await fetch_entity_type_workflow_forms({
    entity_workflow_id: workflow_id,
    workflow_id
  }).catch(() => []);
  const form_ids = get_assigned_workflow_form_ids(workflow_forms, config_form_ids);
  if (!form_ids.length) return [];

  const form_status_results = await Promise.allSettled(
    form_ids.map((form_id) => fetch_assign_form_status_data({ form_id }))
  );
  const assigned_status_order_map = build_form_scoped_statuses(form_status_results);
  if (!assigned_status_order_map.size) return [];

  const workflow_statuses = await fetch_workflow_statuses({ entity_workflow_id: workflow_id }).catch(() => []);
  return map_workflow_ordered_form_status_group(assigned_status_order_map, workflow_statuses);
}

export const configSidebarMethods = {
  is_exact_entity_role() {
    return String(this.current_role || '').trim().toLowerCase() === 'entity';
  },
  async load_onboarding_sidebar_counts() {
    show_sidebar_loader('Loading sidebar...');
    try {
      if (this.is_exact_entity_role()) {
        await this.load_applicant_onboarding_sidebar_counts();
        return;
      }

      const [cases, entity_types] = await Promise.all([
        fetch_queue_onboarding_cases({ include_runtime_progress: false }).catch(() => null),
        fetch_onboarding_entity_types().catch(() => [])
      ]);
      const source_cases = has_sidebar_case_data(cases)
        ? cases
        : this.latest_onboarding_sidebar_cases;
      const normalized_cases = normalize_sidebar_cases(source_cases);

      if (!normalized_cases.length && this.onboarding_gate_items.length) {
        return;
      }

      const entity_type_items = this.build_onboarding_entity_type_items(entity_types);
      const scoped_data = get_scoped_sidebar_data(
        normalized_cases,
        entity_type_items,
        this.$route,
        this.selected_onboarding_case_scope
      );
      const scoped_gate_entity_types = get_gate_entity_type_items(
        scoped_data.scoped_entity_type_items,
        this.selected_onboarding_case_scope
      );

      this.onboarding_entity_type_items = entity_type_items;
      this.onboarding_count_badges = this.build_onboarding_count_badges(scoped_data.scoped_cases, entity_type_items);
      this.onboarding_journey_count_badges = this.build_onboarding_count_badges(normalized_cases, entity_type_items);
      await this.hydrate_onboarding_gate_items(scoped_gate_entity_types, scoped_data.scoped_cases);
    } finally {
      hide_sidebar_loader(this);
    }
  },
  handle_onboarding_sidebar_cases_loaded(event = {}) {
    if (!this.uses_onboarding_system_sidebar) return;
    const cases = Array.isArray(event?.detail?.cases) ? event.detail.cases : [];
    this.latest_onboarding_sidebar_cases = cases;
    apply_loaded_sidebar_cases(this, cases);
  },
  async load_applicant_onboarding_sidebar_counts() {
    const cases = await fetch_all_assigned_onboarding_cases({ _: Date.now() }).catch(() => this.latest_onboarding_sidebar_cases || []);
    this.apply_applicant_onboarding_sidebar_cases(cases);
  },
  async apply_applicant_onboarding_sidebar_cases(cases = []) {
    const source_cases = has_sidebar_case_data(cases)
      ? cases
      : this.latest_onboarding_sidebar_cases;
    const applicant_cases = normalize_sidebar_cases(source_cases);
    if (!applicant_cases.length && this.onboarding_gate_items.length) {
      return;
    }
    const entity_type_items = build_applicant_entity_type_items(applicant_cases);
    const scoped_data = get_scoped_sidebar_data(
      applicant_cases,
      entity_type_items,
      this.$route,
      this.selected_onboarding_case_scope
    );
    const entity_types = unique_applicant_entity_types(scoped_data.scoped_cases);
    const case_gate_items = this.build_case_status_gate_items(scoped_data.scoped_cases);
    const configured_gate_items = await this.build_onboarding_gate_items(entity_types, scoped_data.scoped_cases).catch(() => []);
    const gate_items = merge_applicant_gate_items(this, case_gate_items, configured_gate_items);
    this.onboarding_entity_type_items = entity_type_items;
    this.onboarding_gate_items = gate_items.map((gate_item) => applicant_gate_route(
      gate_item,
      applicant_cases,
      this.selected_onboarding_case_scope
    ));
    this.onboarding_count_badges = { 'my-onboardings': String(scoped_data.scoped_cases.length || 0) };
    this.onboarding_journey_count_badges = this.build_onboarding_count_badges(applicant_cases, entity_type_items);
  },
  apply_onboarding_sidebar_cases(cases = [], options = {}) {
    const should_show_loader = options?.show_loader !== false;
    if (should_show_loader) {
      show_sidebar_loader('Loading sidebar...');
    }
    const source_cases = get_sidebar_source_cases(this, cases);
    const normalized_cases = normalize_sidebar_cases(source_cases);
    if (!normalized_cases.length && this.onboarding_gate_items.length) {
      if (should_show_loader) hide_sidebar_loader(this);
      return;
    }
    const entity_type_items = this.onboarding_entity_type_items || [];
    const scoped_data = get_scoped_sidebar_data(
      normalized_cases,
      entity_type_items,
      this.$route,
      this.selected_onboarding_case_scope
    );
    const scoped_gate_entity_types = get_gate_entity_type_items(
      scoped_data.scoped_entity_type_items,
      this.selected_onboarding_case_scope
    );

    this.onboarding_count_badges = this.build_onboarding_count_badges(scoped_data.scoped_cases, entity_type_items);
    this.onboarding_journey_count_badges = this.build_onboarding_count_badges(normalized_cases, entity_type_items);
    if (scoped_gate_entity_types.length) {
      this.hydrate_onboarding_gate_items(scoped_gate_entity_types, scoped_data.scoped_cases)
        .finally(() => {
          if (should_show_loader) hide_sidebar_loader(this);
        });
      return;
    }
    this.onboarding_gate_items = this.build_case_status_gate_items(scoped_data.scoped_cases);
    if (should_show_loader) hide_sidebar_loader(this);
  },
  handle_onboarding_case_scope_selected(event = {}) {
    const scope = build_case_scope_from_case(event?.detail?.case_item || event?.detail || {});
    if (!scope) return;
    this.selected_onboarding_case_scope = scope;
    if (!has_sidebar_case_data(this.latest_onboarding_sidebar_cases)) {
      this.load_onboarding_sidebar_counts().catch(ignore_sidebar_refresh_error);
      return;
    }
    if (this.is_exact_entity_role()) {
      this.apply_applicant_onboarding_sidebar_cases(this.latest_onboarding_sidebar_cases)
        .catch(ignore_sidebar_refresh_error);
      return;
    }
    this.apply_onboarding_sidebar_cases(this.latest_onboarding_sidebar_cases, { show_loader: false });
  },
  handle_onboarding_case_scope_cleared() {
    if (!this.selected_onboarding_case_scope) return;
    this.selected_onboarding_case_scope = null;
    if (!has_sidebar_case_data(this.latest_onboarding_sidebar_cases)) {
      this.load_onboarding_sidebar_counts().catch(ignore_sidebar_refresh_error);
      return;
    }
    if (this.is_exact_entity_role()) {
      this.apply_applicant_onboarding_sidebar_cases(this.latest_onboarding_sidebar_cases)
        .catch(ignore_sidebar_refresh_error);
      return;
    }
    this.apply_onboarding_sidebar_cases(this.latest_onboarding_sidebar_cases, { show_loader: false });
  },
  handle_onboarding_data_updated() {
    if (!this.uses_onboarding_system_sidebar) return;
    this.load_onboarding_sidebar_counts();
  },
  async hydrate_onboarding_gate_items(entity_types = [], cases = []) {
    const gate_load_token = Number(this._sidebar_gate_load_token || 0) + 1;
    this._sidebar_gate_load_token = gate_load_token;

    const gate_items = await this.build_onboarding_gate_items(entity_types, cases).catch(() => []);
    if (!should_apply_gate_items(this, gate_load_token, gate_items)) return;
    this.onboarding_gate_items = resolve_hydrated_gate_items(this, gate_items, cases);
  },
  async build_onboarding_gate_items(entity_types = [], cases = []) {
    const configured_entity_types = get_configured_entity_types(entity_types);
    if (!configured_entity_types.length) return this.build_case_status_gate_items(cases);

    const workflow_results = await Promise.allSettled(
      configured_entity_types.map((entity_type) => fetch_onboarding_execution_config({
        entity_type_id: Number(entity_type.id)
      }))
    );
    const status_requests = get_fulfilled_config_responses(workflow_results)
      .flatMap(build_config_workflow_status_requests);
    if (!status_requests.length) return [];

    const workflow_status_results = await Promise.allSettled(
      status_requests
    );
    const workflow_status_groups = workflow_status_results.map(get_workflow_status_group);

    const gate_items = collect_gate_items(this, workflow_status_groups, cases);
    return gate_items;
  },
  build_case_status_gate_items(cases = []) {
    const gate_map = new Map();
    (Array.isArray(cases) ? cases : []).forEach((case_item) => {
      const status_label = get_case_status_label(case_item);
      if (!status_label) return;
      this.merge_gate_items(gate_map, this.build_gate_item({ status_name: status_label }, cases));
    });

    return Array.from(gate_map.values()).sort(this.sort_gate_items);
  },
  build_onboarding_entity_type_items(entity_types = []) {
    const journey_items = (Array.isArray(entity_types) ? entity_types : [])
      .filter((entity_type) => (
        entity_type?.id
        && entity_type?.is_active !== false
        && !entity_type?.is_unconfigured
      ))
      .map(build_entity_type_item);

    return [
      {
        id: 'all-journeys',
        label: 'All Journeys',
        meta: 'Manager / admin view across journeys',
        icon: 'bi-grid',
        icon_tone: 'teal',
        route_name: 'MyQueue',
        route_query: { journey_label: 'All Journeys' },
        tone: 'neutral',
        count: '0'
      },
      ...journey_items
    ];
  },
  toggle_current_journey_menu() {
    this.current_journey_menu_open = !this.current_journey_menu_open;
  },
  close_current_journey_menu() {
    this.current_journey_menu_open = false;
  },
  select_current_journey(item) {
    this.current_journey_menu_open = false;
    persist_onboarding_journey_filter(item);
    this.$router.push(this.build_current_journey_route(item));
    this.close_sidebar();
  },
  sync_current_journey_filter_from_route() {
    sync_onboarding_journey_filter_from_route(this.$route);
  },
  build_current_journey_route(item) {
    if (this.$route.name === 'CommandCentre') {
      return this.build_command_centre_journey_route(item);
    }

    return this.build_sidebar_route(item);
  },
  build_command_centre_journey_route(item) {
    const query = item?.id === 'all-journeys'
      ? this.build_all_journeys_query()
      : this.build_selected_journey_query(item);
    const route = {
      name: 'CommandCentre',
      query
    };

    return route;
  },
  build_all_journeys_query() {
    const query = { journey_label: 'All Journeys' };
    return query;
  },
  build_selected_journey_query(item) {
    const query = {
      journey: String(item?.id || ''),
      journey_label: item?.label || 'Entity type'
    };
    return query;
  },
  build_sidebar_route(item) {
    const route_name = get_sidebar_route_name(this.$router, item);
    const route = { name: route_name };
    if (item?.route_params) route.params = item.route_params;
    const journey_query = get_sidebar_journey_query(this, item, route_name);
    const route_query = { ...journey_query, ...(item?.route_query || {}) };
    if (has_query_values(route_query)) route.query = route_query;
    return route;
  },
  sign_out() {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('otp_verified');
    sessionStorage.removeItem('is_aic_one_login');
    sessionStorage.removeItem('auth_redirect_reason');
    this.$store.dispatch('resetStore');
    this.$router.push({ name: 'UserLogin' });
  },
  close_sidebar() {
    if (window.innerWidth < 992) this.$emit('close');
  },
  handle_sidebar_item_click(item = {}) {
    const is_gate_route = String(item?.route_name || '').startsWith('OnboardingGate');
    if (is_gate_route) show_onboarding_system_loader(`Loading ${item?.label || 'gate'}...`);
    this.close_sidebar();
  },
  open_case_owner_profile_modal() {
    this.show_case_owner_profile_modal = true;
  },
  go_to_requestor_new_case() {
    this.$router.push({ name: 'OwnerQueue' });
    this.close_sidebar();
  },
  close_case_owner_profile_modal() {
    this.show_case_owner_profile_modal = false;
  },
  handle_case_owner_profile_edit() {
    this.show_case_owner_profile_modal = false;
    this.$router.push({ name: 'OwnerDashboard' });
    this.close_sidebar();
  },
  is_active_route(route_name, route_query = null) {
    if (!this.$router.hasRoute(route_name)) return false;
    const current_route_name = String(this.$route.name || '').trim();
    const current_route_status = String(this.$route.query?.status || '').trim();

    if (current_route_name === 'MyQueueForm' && current_route_status) {
      if (isMyQueueFormRoute(route_name)) return false;
      if (isStatusQueueRoute(route_name) && route_query?.status) {
        return this.SIDEBAR_ROUTE_QUERY_KEYS.every((key) => does_route_query_key_match(this, route_query, key));
      }
    }

    const active_route_names = getActiveRouteNames(route_name);
    if (!active_route_names.includes(current_route_name)) return false;
    if (!route_query) {
      if (current_route_name !== route_name) return true;
      return ['status', 'mode'].every((key) => is_empty_current_query_value(this, key));
    }

    return this.SIDEBAR_ROUTE_QUERY_KEYS.every((key) => does_route_query_key_match(this, route_query, key));
  },
  build_onboarding_count_badges(cases = [], entity_type_items = []) {
    return this._build_onboarding_count_badges(cases, entity_type_items);
  },
  build_gate_item(status = {}, cases = []) {
    return this._build_gate_item(status, cases);
  },
  merge_gate_items(gate_map, gate_item) {
    return this._merge_gate_items(gate_map, gate_item);
  },
  sort_gate_items(left_item, right_item) {
    return this._sort_gate_items(left_item, right_item);
  }
};
